import React, { useState, useMemo } from "react";
import Typography from "style-guide/component/Typography";
import Button from "style-guide/component/Button";
import Input from "style-guide/component/Input";
import { FAQ_DATA, QUICK_ACTIONS } from "./constants";
import styles from "./HelpSupport.module.css";

interface FAQSectionProps {
  className?: string;
}

const FAQSection: React.FC<FAQSectionProps> = ({ className = "" }) => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");

  const categories = useMemo(() => {
    const cats = FAQ_DATA.map(faq => faq.category);
    return ["all", ...Array.from(new Set(cats))];
  }, []);

  const filteredFAQs = useMemo(() => {
    let filtered = FAQ_DATA;
    
    // Filter by category
    if (selectedCategory !== "all") {
      filtered = filtered.filter(faq => faq.category === selectedCategory);
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(faq => 
        faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        faq.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }
    
    return filtered;
  }, [selectedCategory, searchTerm]);

  const toggleFAQ = (id: number) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  const handleQuickAction = (category: string) => {
    setSelectedCategory(category);
    setSearchTerm("");
  };

  const clearSearch = () => {
    setSearchTerm("");
    setSelectedCategory("all");
  };

  return (
    <div className={className}>
      {/* Quick Action Buttons */}
      <div className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 mb-8">
        <Typography variant="Heading 1" text="Help & Support" className="mb-3" />
        <Typography
          variant="Base"
          text="Find answers to common questions or get in touch with our support team"
          className="text-gray-600"
        />
        
        <div className="flex flex-wrap justify-center gap-3 mt-6">
          {QUICK_ACTIONS.map((action) => (
            <Button
              key={action.id}
              type={selectedCategory === action.category ? "Primary" : "Default"}
              buttonText={action.label}
              onClick={() => handleQuickAction(action.category)}
              size="Small"
              className={selectedCategory === action.category ? "shadow-md" : ""}
            />
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
          <Typography variant="Heading 2" text="Frequently Asked Questions" className="mb-4 lg:mb-0" />
          <div className="text-sm text-gray-500 bg-blue-50 px-3 py-1 rounded-full">
            {filteredFAQs.length} question{filteredFAQs.length !== 1 ? 's' : ''} found
          </div>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1">
            <Input
              type="text"
              placeholder="🔍 Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full transition-all duration-200 ${
                searchTerm.trim() 
                  ? "border-blue-500 bg-blue-50 shadow-sm" 
                  : ""
              }`}
            />
          </div>
          <div className="md:w-48">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className={`w-full h-full px-4 py-2 border rounded-md bg-white text-sm focus:border-blue-500 focus:outline-none transition-all duration-200 ${
                selectedCategory !== "all" 
                  ? "border-blue-500 bg-blue-50 text-blue-700 font-medium shadow-sm" 
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              {categories.map((category) => (
                <option key={category} value={category}>
                  {category === "all" ? "All Categories" : category}
                </option>
              ))}
            </select>
          </div>
          {(searchTerm.trim() || selectedCategory !== "all") && (
            <div className="flex items-center">
              <Button
                type="Default"
                buttonText="Clear Filters"
                onClick={clearSearch}
                size="Small"
                className="h-full text-gray-500 hover:text-gray-700"
              />
            </div>
          )}
        </div>

        {/* FAQ Results */}
        {filteredFAQs.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-4">🔍</div>
            <Typography variant="Base" text="No FAQs found matching your search." className="mb-2" />
            <Typography variant="SM" text="Try different keywords or browse all categories." />
            <Button
              type="Text"
              buttonText="Clear Search"
              onClick={clearSearch}
              className="mt-3"
            />
          </div>
        ) : (
          <div className="space-y-3">
            {filteredFAQs.map((faq) => (
              <div
                key={faq.id}
                className={`border rounded-xl overflow-hidden hover:shadow-md transition-shadow ${expandedFAQ === faq.id ? "border-blue-primary" : "border-gray-300"}`}
              >
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  className="w-full px-6 py-4 text-left bg-gray-50 hover:bg-gray-100 transition-colors flex justify-between items-start gap-4"
                >
                  <div className="flex-1">
                    <Typography variant="Heading 4" text={faq.question} className="font-medium mb-2" />
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
                        {faq.category}
                      </span>
                    </div>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="text-2xl text-gray-400 transform transition-transform duration-200" 
                          style={{
                            transform: expandedFAQ === faq.id ? 'rotate(45deg)' : 'rotate(0deg)'
                          }}>
                      +
                    </span>
                  </div>
                </button>
                {expandedFAQ === faq.id && (
                  <div className={`px-6 py-5 bg-white border-t border-gray-100 ${styles.fadeIn}`}>
                    <Typography
                      variant="Base"
                      text={faq.answer}
                      className="text-gray-700 leading-relaxed mb-3"
                    />
                    <div className="flex flex-wrap gap-1">
                      {faq.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQSection;
